import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Input — campo de texto base.
 *
 * @typedef {Object} InputProps
 * @property {string} [label] Rótulo do campo.
 * @property {string} [id] Id do input (para associação label/input).
 * @property {boolean} [error=false] Estado de erro (borda vermelha).
 * @property {boolean} [disabled=false] Desabilita o campo.
 * @property {string} [hint] Texto de ajuda abaixo do campo.
 * @property {string} [errorMessage] Mensagem de erro exibida abaixo do campo.
 * @property {React.ReactNode} [leftIcon] Ícone à esquerda.
 * @property {React.ReactNode} [rightIcon] Ícone à direita.
 * @property {string} [className] Classes adicionais.
 */

export function Input({
  label,
  id,
  error = false,
  disabled = false,
  hint,
  errorMessage,
  leftIcon,
  rightIcon,
  className,
  ...props
}) {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

  return (
    <div className="block">
      {label && (
        <label htmlFor={inputId} className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-soft">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint">
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          disabled={disabled}
          aria-invalid={error || undefined}
          aria-describedby={
            errorMessage ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
          }
          className={cn(
            'h-12 w-full rounded-xl border bg-white px-4 text-base text-ink',
            'placeholder:text-ink-faint',
            'transition-colors duration-200 ease-out',
            'focus:outline-none focus:ring-2',
            leftIcon && 'pl-11',
            rightIcon && 'pr-11',
            error
              ? 'border-danger focus:border-danger focus:ring-danger/20'
              : 'border-slate-200 focus:border-primary focus:ring-primary/20',
            disabled && 'cursor-not-allowed bg-slate-50 text-ink-faint',
            className
          )}
          {...props}
        />
        {rightIcon && (
          <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-faint">
            {rightIcon}
          </span>
        )}
      </div>
      {errorMessage && (
        <p id={`${inputId}-error`} role="alert" className="mt-1.5 text-xs font-medium text-danger">
          {errorMessage}
        </p>
      )}
      {!errorMessage && hint && (
        <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-ink-faint">
          {hint}
        </p>
      )}
    </div>
  );
}
